import React, { useState, useMemo } from 'react';
import { Button } from '../../Atoms';
import DropDown from '../../Atoms/DropDown';
import CustomizedDatePicker from '../../Atoms/DatePicker';
import dayjs from 'dayjs';

const generateExpiryOptions = () => {
    const now = dayjs();
    return [
        ...([1, 2, 3, 4, 5, 6] as const).map((months) => {
            const date = now.add(months, 'month').format('DD/MM/YY');
            const label = `${months} month${months > 1 ? 's' : ''}`;
            const value = `${months}_month${months > 1 ? 's' : ''}`;
            return {
                value,
                label,
                date,
                isoDate: now.add(months, 'month').toISOString(),
                component: (isFocused: boolean) => (
                    <span className={`option ${isFocused && 'active'}`}>
                        {label}
                        <br />
                        <span className={`description ${isFocused && 'active'}`}>{date}</span>
                    </span>
                ),
            };
        }),
        {
            value: 'custom_date',
            label: 'Custom date',
            date: '',
            isoDate: null,
            component: (isFocused: boolean) => (
                <span className={`option ${isFocused && 'active'}`}>Custom date</span>
            ),
        },
    ];
};

interface PassModalProps {
    isOpen: boolean;
    onClose: (result: { reason: string; notes: string } | null) => void;
    handleSubmit: (selectedDate: string) => void;
    disabled?: boolean;
    name: string;
}

const PassModal: React.FC<PassModalProps> = ({ isOpen, onClose, handleSubmit, name, disabled }) => {
    const POA_EXPIRY_OPTIONS = useMemo(() => generateExpiryOptions(), []);
    const [selectedOption, setSelectedOption] = useState<string>('');
    const [customDate, setCustomDate] = useState<string | null>(null);

    const handleOptionChange = (val: string) => {
        setSelectedOption(val);
    };

    const onHandleSubmit = async () => {
        try {
            let selectedDate: string;
            
            if (selectedOption === 'custom_date') {
                selectedDate = dayjs(customDate).toISOString();
            } else {
                const option = POA_EXPIRY_OPTIONS.find(opt => opt.value === selectedOption);
                selectedDate = option?.isoDate as string;
            }
            
            handleSubmit(selectedDate);
        } catch (error) {
            console.error('Error submitting fail reason:', error);
            onClose(null);
        }
    };

    const handleCancelOnClick = () => {
        onClose(null);
        setSelectedOption('');
    };

    const getIfSubmitDisabled = () => {
        if (!selectedOption) return true;
        if (selectedOption === 'custom_date' && !customDate) return true;
        return false;
    }

    if (!isOpen) return null;

    return (
        <div className="complete-modal complete-modal modal-overlay" onClick={() => onClose(null)}>
            <div className="modal-content pass" onClick={(e) => e.stopPropagation()}>
                <div className='modal-form'>
                    <div className='form-content'>
                        <h1 className='modal-header'>{name}</h1>
                        <div className='content'>
                            <p>
                                When you complete the POA for this patient, the outcome will be set to <span className='highlight'>Pass</span>
                            </p>
                            <div className='dropdown-container'>
                                <label>Select the POA expiry date</label>
                                <DropDown
                                    options={POA_EXPIRY_OPTIONS}
                                    renderValue={(option: { label: string; date: string }) =>
                                        option.date ? `${option.label} - ${option.date}` : option.label
                                    }
                                    value={POA_EXPIRY_OPTIONS.find(o => o.value === selectedOption) || ''}
                                    handleChange={(selected: { value: string; label: string }) =>
                                        handleOptionChange(selected?.value ?? '')
                                    }
                                    isInValid={false}
                                    placeholder="Select"
                                    isSearchable={false}
                                />
                            </div>
                            {selectedOption === 'custom_date' && (
                                <div className='dropdown-container w-[336px]'>
                                    <label>Select the custom date</label>
                                    <CustomizedDatePicker
                                        onChange={(date: string | null) => setCustomDate(date)}
                                        initialDate={customDate}
                                    />
                                </div>
                            )}
                            <p>
                                A PDF of the POA will be downloaded, and needs to be added to TrakCare
                            </p>
                        </div>
                    </div>
                    <div className='button-container button-container-triage-list button-spacing'>
                        <Button
                            type='tertiary'
                            text="Complete this POA"
                            icon={false}
                            
                            handleClick={onHandleSubmit}
                            stateProp={disabled ||  getIfSubmitDisabled() ? 'disabled' : 'enabled'}
                        />
                        <Button
                            type='primary'
                            text="Cancel"
                            icon={false}
                            handleClick={handleCancelOnClick}
                            stateProp='enabled'
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PassModal;
